/**
 * 反算：根据目标 (F, S) 选出 Top-N 电机/传动方案
 *
 * 算法概要（详见 docs/03-电机选型工具设计.md §三）：
 *   1) 功率剪枝：P_min = F × k_f × S × k_s × g / (η × 1000)
 *   2) 对每台 P ≥ P_min 的电机，枚举导程 L
 *   3) 对每个 (motor, L)：
 *        i_max = R × L / (60 × S × k_s)            // 速度余量 k_s 抬高速度下界
 *        i_min = (F × k_f × L × g) / (T × η × 2π × 1000)  // 负载余量 k_f 抬高推力下界
 *      若 i_min > i_max 则跳过
 *   4) 在候选 (reducer × syncDec × syncInc) 中取所有 i = r·sd/si 落在 [i_min, i_max] 的组合
 *   5) 计算每个候选的实际 F、S、综合得分（越小越好）
 *   6) 排序取前 N
 */
import { DEFAULT_EFFICIENCY, DEFAULT_GRAVITY, DEFAULT_LEADS, DEFAULT_LOAD_SAFETY_FACTOR, DEFAULT_PRICE_CONFIG, DEFAULT_REDUCERS, DEFAULT_SCORE_WEIGHTS, DEFAULT_SPEED_SAFETY_FACTOR, DEFAULT_SYNC_DEC_RATIOS, DEFAULT_SYNC_INC_RATIOS, DEFAULT_TOP_N, } from './product-spec.js';
import { DEFAULT_MOTOR_LIBRARY } from './motor-library.js';
const TWO_PI = 2 * Math.PI;
const EPS = 1e-9;
/** 传动复杂度惩罚 */
function ratioPenalty(reducer, syncDec, syncInc) {
    const hasReducer = reducer !== 1;
    const hasSync = syncDec !== 1 || syncInc !== 1;
    if (hasReducer && hasSync)
        return 0.5;
    if (hasSync)
        return 0.3;
    if (hasReducer)
        return 0.1;
    return 0.0;
}
/** 电机价格：优先用 motor.price，否则按 power × 单瓦系数估算 */
function motorPrice(motor, cfg) {
    return motor.price ?? motor.power * cfg.motorPricePerWatt;
}
/** 减速机价格：按比值落在哪一档（升序首个 maxRatio ≥ ratio 的档位）取价 */
function reducerPrice(ratio, cfg) {
    for (const band of cfg.reducerPriceBands) {
        if (ratio <= band.maxRatio + EPS)
            return band.price;
    }
    // 超出最大档（理论不应到这里，分档表覆盖到 100）取最后一档价
    return cfg.reducerPriceBands[cfg.reducerPriceBands.length - 1].price;
}
/** 同步轮价格：dec 或 inc 任一不为 1 计入 */
function syncPrice(syncDec, syncInc, cfg) {
    return syncDec !== 1 || syncInc !== 1 ? cfg.syncPulleyPrice : 0;
}
/** 综合价格因子（归一化）：(电机 + 减速机 + 同步轮) / 参考价 */
function priceFactor(motor, reducer, syncDec, syncInc, cfg) {
    const total = motorPrice(motor, cfg) + reducerPrice(reducer, cfg) + syncPrice(syncDec, syncInc, cfg);
    return total / cfg.referencePrice;
}
function scoreCandidate(ctx) {
    const { motorPower, pRequired, fActual, fScaled, sActual, sScaled } = ctx;
    // 各分项原始值（未加权）—— 推力/速度贴合误差均相对各自的"安全下界"
    const powerPart = motorPower / pRequired - 1;
    const thrustPart = Math.abs(fActual / fScaled - 1);
    const speedPart = Math.abs(sActual / sScaled - 1);
    const ratioPart = ratioPenalty(ctx.reducer, ctx.syncDec, ctx.syncInc);
    const pricePart = priceFactor(ctx.motor, ctx.reducer, ctx.syncDec, ctx.syncInc, ctx.priceCfg);
    const w = ctx.weights;
    const total = w.power * powerPart +
        w.thrust * thrustPart +
        w.speed * speedPart +
        w.ratio * ratioPart +
        w.price * pricePart;
    return {
        total,
        breakdown: {
            power: powerPart,
            thrust: thrustPart,
            speed: speedPart,
            ratio: ratioPart,
            price: pricePart,
        },
    };
}
export function selectMotor(target, options = {}) {
    const F = target.F;
    const S = target.S;
    if (!Number.isFinite(F) || F <= 0)
        throw new Error('target.F must be > 0');
    if (!Number.isFinite(S) || S <= 0)
        throw new Error('target.S must be > 0');
    const efficiency = options.efficiency ?? DEFAULT_EFFICIENCY;
    const gravity = options.gravity ?? DEFAULT_GRAVITY;
    const loadSafetyFactor = options.loadSafetyFactor ?? DEFAULT_LOAD_SAFETY_FACTOR;
    const speedSafetyFactor = options.speedSafetyFactor ?? DEFAULT_SPEED_SAFETY_FACTOR;
    const leads = options.leads ?? DEFAULT_LEADS;
    const reducers = options.reducers ?? DEFAULT_REDUCERS;
    const syncDecRatios = options.syncDecRatios ?? DEFAULT_SYNC_DEC_RATIOS;
    const syncIncRatios = options.syncIncRatios ?? DEFAULT_SYNC_INC_RATIOS;
    const motors = options.motors ?? DEFAULT_MOTOR_LIBRARY;
    const topN = options.topN ?? DEFAULT_TOP_N;
    const weights = options.weights ?? DEFAULT_SCORE_WEIGHTS;
    const priceCfg = options.priceConfig ?? DEFAULT_PRICE_CONFIG;
    const fScaled = F * loadSafetyFactor; // 推力可行性下界（实际推力须 ≥ 此值）
    const sScaled = S * speedSafetyFactor; // 速度可行性下界（实际速度须 ≥ 此值）
    // 功率剪枝阈值（机械功率，W）
    // 推导：F·S·g = η · P_motor · 1000 → P_min = F_scaled · S_scaled · g / (η · 1000)
    const pRequired = (fScaled * sScaled * gravity) / (efficiency * 1000);
    const candidates = [];
    for (const motor of motors) {
        if (motor.power < pRequired)
            continue;
        const T = motor.ratedTorque;
        const R = motor.ratedSpeed;
        const motorPower = (T * TWO_PI * R) / 60;
        for (const lead of leads) {
            // 速度上界：i 越大速度越慢，i 不能超过此值（含速度余量）
            const iMax = (R * lead) / (60 * sScaled);
            // 推力下界：i 越小推力越小，i 不能低于此值（含负载余量）
            const iMin = (fScaled * lead * gravity) / (T * efficiency * TWO_PI * 1000);
            if (iMin > iMax + EPS)
                continue;
            for (const reducer of reducers) {
                for (const syncDec of syncDecRatios) {
                    for (const syncInc of syncIncRatios) {
                        if (syncInc <= 0)
                            continue;
                        const i = (reducer * syncDec) / syncInc;
                        if (i < iMin - EPS || i > iMax + EPS)
                            continue;
                        const fActual = (T * efficiency * TWO_PI * i * 1000) / lead / gravity;
                        const sActual = (R * lead) / (60 * i);
                        // 防御性校验（理论上恒成立，因 i ∈ [iMin, iMax]）
                        if (fActual + EPS < fScaled)
                            continue;
                        if (sActual + EPS < sScaled)
                            continue;
                        const score = scoreCandidate({
                            motorPower,
                            pRequired,
                            fActual,
                            fScaled,
                            sActual,
                            sScaled,
                            reducer,
                            syncDec,
                            syncInc,
                            motor,
                            lead,
                            weights,
                            priceCfg,
                        });
                        candidates.push({
                            rank: 0, // 排序后赋值
                            motor,
                            lead,
                            ratio: { reducer, syncDec, syncInc, total: i },
                            actual: { thrust: fActual, speed: sActual },
                            margin: {
                                thrust: (fActual / F - 1) * 100,
                                speed: (sActual / S - 1) * 100,
                            },
                            score: score.total,
                            scoreBreakdown: score.breakdown,
                        });
                    }
                }
            }
        }
    }
    candidates.sort((a, b) => a.score - b.score);
    const top = candidates.slice(0, topN);
    top.forEach((c, idx) => {
        c.rank = idx + 1;
    });
    return top;
}
//# sourceMappingURL=reverse.js.map